import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Container,
  Chip,
  Tooltip
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

import { image } from "../../common/Theme";
import { Header } from "../../component/Header";
import Apiconnection from "../../utils/Apiconnection";

const BookingDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printableRef = useRef(null);
  const hasTrackedConversion = useRef(false);

  const [isPrinting, setIsPrinting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Initialize booking details from location.state or sessionStorage fallback
  const [bookingDetails, setBookingDetails] = useState(() => {
    if (location.state && Object.keys(location.state).length > 0) {
      try {
        sessionStorage.setItem("last_booking_details", JSON.stringify(location.state));
      } catch (e) {
        console.error("Failed to cache booking details", e);
      }
      return location.state;
    }
    try {
      const cached = sessionStorage.getItem("last_booking_details");
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error("Failed to read cached booking details", e);
    }
    return null;
  });

  useEffect(() => {
    if (location.state && Object.keys(location.state).length > 0) {
      setBookingDetails(location.state);
      try {
        sessionStorage.setItem("last_booking_details", JSON.stringify(location.state));
      } catch (e) {
        console.error("Failed to cache booking details", e);
      }
    }
  }, [location.state]);

  // Google Ads Purchase Conversion Event Tracking
  useEffect(() => {
    if (!hasTrackedConversion.current && bookingDetails) {
      const transactionId = bookingDetails?.bookingRequestDetail?.bookingTicketNo || "";
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-16812216122/OIjgCKr8gfQZELqe2NA-",
          transaction_id: transactionId,
          value:
            bookingDetails?.invoiceCreate?.paidAmount ??
            bookingDetails?.invoiceCreate?.reservationAmount ??
            0,
          currency: "USD",
        });
        hasTrackedConversion.current = true;
      }
    }
  }, [bookingDetails]);

  // Fetch optional API policies in the background
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        await Apiconnection.post("content-page-get-by-type", {
          type: "terms_and_conditions",
        });
      } catch (error) {
        // Silently continue since static fallback policies are already embedded
      }
    };
    fetchPolicies();
  }, []);

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("ddd, MMM D, YYYY") : dateStr;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "--";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
    const t = dayjs(`2000-01-01 ${timeStr}`);
    return t.isValid() ? t.format("h:mm A") : timeStr;
  };

  const req = bookingDetails?.bookingRequestDetail;
  const inv = bookingDetails?.invoiceCreate;
  const user = req?.user;

  // Calculate total days
  const fromDate = req?.fromDate;
  const toDate = req?.toDate;
  const calculateTotalDays = () => {
    if (!fromDate || !toDate) return 1;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return isNaN(diff) || diff < 1 ? 1 : diff;
  };
  const totalDays = calculateTotalDays();

  const ticketNo = req?.bookingTicketNo || "N/A";
  const isGuest =
    user?.role?.roleName === "Guest" ||
    (!user?.role?.roleName && !localStorage.getItem("prime_access_token"));
  const customerName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    (isGuest ? "Guest Customer" : "Valued Customer");
  const licensePlate =
    req?.plateNumber ||
    req?.licensePlateNumber ||
    req?.plateNo ||
    req?.licensePlate ||
    bookingDetails?.plateNumber ||
    bookingDetails?.licensePlateNumber ||
    bookingDetails?.customerdetails?.plateNumber ||
    "N/A";
  const vehicleType = req?.vehicleType || req?.vehicleTypeName || "Sedan";
  const checkInTime = req?.selectedCheckInTime;
  const checkOutTime = req?.selectedCheckOutTime;

  const dueAmount = inv?.dueAmount ?? 15;
  const paidAmount = inv?.paidAmount ?? inv?.reservationAmount ?? 25;
  const totalAmount =
    inv?.totalAmount ?? (Number(paidAmount) + Number(dueAmount));

  const handleCopyTicket = () => {
    if (!ticketNo || ticketNo === "N/A") return;
    navigator.clipboard.writeText(ticketNo);
    setCopied(true);
    toast.success("Booking ID copied to clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  const handlePrintPdf = () => {
    if (!printableRef.current) return;
    setIsPrinting(true);
    const element = printableRef.current;
    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `PrimePark_Pass_${ticketNo}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .then(() => {
        setIsPrinting(false);
        toast.success("Booking pass downloaded successfully!");
      })
      .catch((err) => {
        console.error("PDF generation failed:", err);
        setIsPrinting(false);
        toast.error("Could not download PDF. You can also print the pass.");
      });
  };

  const handleNativePrint = () => {
    window.print();
  };

  // If completely empty booking details
  if (!bookingDetails) {
    return (
      <div
        className="booking-main"
        style={{
          backgroundColor: "#001b44",
          backgroundImage: `url(${image.bodybg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          paddingBottom: "60px",
        }}
      >
        <div className="booking-inner" style={{ maxWidth: "1280px", margin: "0 auto", padding: "20px 16px" }}>
          <Header />
          <Container maxWidth="md" sx={{ mt: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: "24px",
                textAlign: "center",
                backgroundColor: "#fff",
                boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: "60px", color: "#004777", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>
                No Active Booking Found
              </Typography>
              <Typography sx={{ color: "#64748b", mb: 4, maxWidth: "500px", mx: "auto" }}>
                It seems like you reached this page without an active booking session. If you recently made a booking, please check your confirmation email or log in to view your reservations.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/booking-create")}
                  sx={{
                    backgroundColor: "#004777",
                    borderRadius: "100px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Book a Parking Spot
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderColor: "#004777",
                    color: "#004777",
                    borderRadius: "100px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            </Paper>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div
      className="booking-main"
      style={{
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Background Top Header Bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "360px",
          backgroundColor: "#001b44",
          backgroundImage: `url(${image.bodybg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 0,
        }}
      />

      <div
        className="booking-inner"
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: "1320px",
          margin: "0 auto",
          padding: "20px 16px 60px 16px",
        }}
      >
        <Header />

        <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 3 }, px: { xs: 0, sm: 2 } }}>
          {/* Main White Card Shell */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: { xs: "20px", md: "32px" },
              p: { xs: 2.5, sm: 4, md: 5 },
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.12)",
              fontFamily: "'Inter', 'Poppins', sans-serif",
            }}
          >
            {/* Top Confirmation Hero Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2.5,
                pb: 3.5,
                borderBottom: "1px solid #e2e8f0",
                mb: 4,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: { xs: 52, sm: 64 },
                    height: { xs: 52, sm: 64 },
                    borderRadius: "50%",
                    backgroundColor: "#ecfdf5",
                    border: "2px solid #a7f3d0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleRoundedIcon
                    sx={{ color: "#10b981", fontSize: { xs: 34, sm: 42 } }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "#0f172a",
                        fontSize: { xs: "24px", sm: "30px", md: "32px" },
                        letterSpacing: "-0.5px",
                      }}
                    >
                      Booking Confirmed!
                    </Typography>
                    <Chip
                      label="CONFIRMED"
                      size="small"
                      sx={{
                        backgroundColor: "#dcfce7",
                        color: "#15803d",
                        fontWeight: 800,
                        fontSize: "12px",
                        letterSpacing: "0.5px",
                        height: "24px",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", fontWeight: 500, mt: 0.5, fontSize: { xs: "13px", sm: "14px" } }}
                  >
                    Your parking reservation is confirmed. Keep your Booking ID handy upon arrival.
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handlePrintPdf}
                  disabled={isPrinting}
                  sx={{
                    flex: { xs: 1, sm: "initial" },
                    backgroundColor: "#004777",
                    color: "white",
                    borderRadius: "100px",
                    px: 3,
                    py: 1.3,
                    fontWeight: 700,
                    fontSize: "14px",
                    textTransform: "none",
                    boxShadow: "0 6px 18px rgba(0, 71, 119, 0.25)",
                    "&:hover": {
                      backgroundColor: "#003355",
                    },
                  }}
                >
                  {isPrinting ? "Generating..." : "Download Pass (PDF)"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintOutlinedIcon />}
                  onClick={handleNativePrint}
                  sx={{
                    flex: { xs: 1, sm: "initial" },
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    borderRadius: "100px",
                    px: 2.5,
                    py: 1.3,
                    fontWeight: 700,
                    fontSize: "14px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  Print
                </Button>
              </Box>
            </Box>

            {/* Main 2-Column Content Grid */}
            <Grid container spacing={{ xs: 3, lg: 4 }}>
              {/* Left Column: Parking Pass & Location */}
              <Grid item xs={12} lg={7}>
                {/* Printable Parking Pass Card */}
                <div ref={printableRef}>
                  <Box
                    sx={{
                      backgroundColor: "#ffffff",
                      border: "2px solid #e2e8f0",
                      borderRadius: "24px",
                      overflow: "hidden",
                      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
                      position: "relative",
                    }}
                  >
                    {/* Pass Top Branding Bar */}
                    <Box
                      sx={{
                        background: "linear-gradient(135deg, #001b44 0%, #004777 100%)",
                        color: "white",
                        px: { xs: 2.5, sm: 3.5 },
                        py: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            backgroundColor: "white",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={image.logo}
                            alt="Prime Park Logo"
                            style={{ height: "28px", objectFit: "contain" }}
                          />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: "14px", letterSpacing: "1px" }}>
                          OFFICIAL PARKING PASS
                        </Typography>
                      </Box>
                      <Chip
                        icon={<CheckRoundedIcon style={{ color: "#10b981", fontSize: "16px" }} />}
                        label="GUARANTEED"
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.15)",
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: "11px",
                          letterSpacing: "0.5px",
                        }}
                      />
                    </Box>

                    {/* Pass Body Content */}
                    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                      {/* Booking ID Header Box */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fafc",
                          border: "1.5px dashed #cbd5e1",
                          borderRadius: "16px",
                          p: 2.5,
                          mb: 3,
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 1.5,
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              color: "#64748b",
                            }}
                          >
                            Booking Reference ID / Voucher #
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: "22px", sm: "26px" },
                              fontWeight: 900,
                              color: "#004777",
                              mt: 0.25,
                            }}
                          >
                            {ticketNo}
                          </Typography>
                        </Box>

                        <Tooltip title={copied ? "Copied!" : "Copy Booking ID"} arrow>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCopyTicket}
                            startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: "16px" }} />}
                            sx={{
                              borderColor: copied ? "#10b981" : "#004777",
                              color: copied ? "#10b981" : "#004777",
                              backgroundColor: copied ? "#ecfdf5" : "white",
                              borderRadius: "100px",
                              px: 2,
                              py: 0.8,
                              fontWeight: 700,
                              textTransform: "none",
                              fontSize: "13px",
                              "&:hover": {
                                backgroundColor: copied ? "#ecfdf5" : "#f0f7ff",
                                borderColor: copied ? "#10b981" : "#003355",
                              },
                            }}
                          >
                            {copied ? "Copied" : "Copy ID"}
                          </Button>
                        </Tooltip>
                      </Box>

                      {/* Drop-off & Pick-up Timing Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              p: 2.5,
                              borderRadius: "16px",
                              backgroundColor: "#f0f7ff",
                              border: "1px solid #bfdbfe",
                              height: "100%",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <CalendarTodayOutlinedIcon sx={{ color: "#004777", fontSize: "18px" }} />
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  color: "#004777",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Drop Off (Check-In)
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>
                              {formatDate(fromDate)}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                              <AccessTimeOutlinedIcon sx={{ color: "#2563eb", fontSize: "16px" }} />
                              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#2563eb" }}>
                                {formatTime(checkInTime)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              p: 2.5,
                              borderRadius: "16px",
                              backgroundColor: "#f0f7ff",
                              border: "1px solid #bfdbfe",
                              height: "100%",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <CalendarTodayOutlinedIcon sx={{ color: "#004777", fontSize: "18px" }} />
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  color: "#004777",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Pick Up (Check-Out)
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>
                              {formatDate(toDate)}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                              <AccessTimeOutlinedIcon sx={{ color: "#2563eb", fontSize: "16px" }} />
                              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#2563eb" }}>
                                {formatTime(checkOutTime)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Duration Banner */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f8fafc",
                          borderRadius: "12px",
                          py: 1,
                          px: 2,
                          mb: 3,
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <AccessTimeOutlinedIcon sx={{ color: "#64748b", fontSize: "16px", mr: 1 }} />
                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>
                          Total Reserved Duration:{" "}
                          <span style={{ color: "#004777", fontWeight: 800 }}>
                            {totalDays} {totalDays === 1 ? "Day" : "Days"}
                          </span>
                        </Typography>
                      </Box>

                      {/* Customer & Vehicle Info Grid */}
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "14px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                              <PersonOutlineOutlinedIcon sx={{ fontSize: "16px", color: "#64748b" }} />
                              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                Customer Name
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
                              {customerName}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500, mt: 0.25 }}>
                              {isGuest ? "Guest Reservation" : "Customer Account"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "14px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                              <DirectionsCarOutlinedIcon sx={{ fontSize: "16px", color: "#64748b" }} />
                              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                Vehicle Type
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
                              {vehicleType}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500, mt: 0.25 }}>
                              Standard Parking
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, borderRadius: "14px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                              <DirectionsCarOutlinedIcon sx={{ fontSize: "16px", color: "#64748b" }} />
                              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                                License Plate
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 0.5 }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: "#ffffff",
                                  border: "2px solid #1e293b",
                                  borderRadius: "6px",
                                  padding: "4px 12px",
                                  fontSize: "14px",
                                  fontWeight: "800",
                                  color: "#0f172a",
                                  lineHeight: "1.2",
                                }}
                              >
                                {licensePlate}
                              </span>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>


                    </Box>
                  </Box>
                </div>

                {/* Facility Location & GPS Directions (Replacing Google Map) */}
                <Box
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "20px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          backgroundColor: "#f0f7ff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnOutlinedIcon sx={{ color: "#004777", fontSize: "24px" }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>
                          Prime Park JFK Parking Facility
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "#64748b", mt: 0.25 }}>
                          15115 Lefferts Blvd, South Ozone Park, NY 11420
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.75, flexWrap: "wrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PhoneOutlinedIcon sx={{ fontSize: "14px", color: "#004777" }} />
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#004777" }}>
                              +1 800-884-0153
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <AirportShuttleOutlinedIcon sx={{ fontSize: "15px", color: "#10b981" }} />
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#10b981" }}>
                              Free 24/7 Terminal Shuttle
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      component="a"
                      href="https://www.google.com/maps/search/?api=1&query=Prime+Park+15115+Lefferts+Blvd+South+Ozone+Park+NY+11420"
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: "16px" }} />}
                      sx={{
                        backgroundColor: "#004777",
                        color: "white",
                        borderRadius: "100px",
                        px: 2.5,
                        py: 1,
                        fontSize: "13px",
                        fontWeight: 700,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "#003355",
                        },
                      }}
                    >
                      Get Directions
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Right Column: Payment Breakdown, Quick Guide, Policies & Registration */}
              <Grid item xs={12} lg={5}>
                {/* Payment Breakdown Card */}
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "20px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <ReceiptLongOutlinedIcon sx={{ color: "#004777", fontSize: "22px" }} />
                    <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#0f172a" }}>
                      Payment Breakdown
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                    <Typography sx={{ fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                      Duration
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                      {totalDays} {totalDays === 1 ? "Day" : "Days"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #f1f5f9" }}>
                    <Typography sx={{ fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                      Estimated Total Charges
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                      ${Number(totalAmount).toFixed(2)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                    <Typography sx={{ fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                      Online Reservation Deposit
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                        ${Number(paidAmount).toFixed(2)}
                      </Typography>
                      <Chip
                        size="small"
                        icon={<CheckRoundedIcon style={{ color: "#15803d", fontSize: "14px" }} />}
                        label="PAID"
                        sx={{
                          backgroundColor: "#dcfce7",
                          color: "#15803d",
                          fontWeight: 800,
                          fontSize: "11px",
                          height: "22px",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Highlighted Amount Due at Location */}
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 2.5,
                      borderRadius: "16px",
                      backgroundColor: "#fffbeb",
                      border: "2px solid #fcd34d",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 800,
                        color: "#92400e",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Amount to be Paid at Location
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "28px", sm: "32px" },
                        fontWeight: 900,
                        color: "#b45309",
                        my: 0.5,
                      }}
                    >
                      ${Number(dueAmount).toFixed(2)}
                      <span style={{ fontSize: "18px", color: "#b91c1c" }}>*</span>
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#78350f", fontWeight: 500 }}>
                      Payable directly to the attendant at drop-off via Cash or Credit/Debit Card.
                    </Typography>
                  </Box>
                </Box>

                {/* Quick Arrival Steps */}
                <Box
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "20px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a", mb: 2 }}>
                    How Arrival & Drop-off Works:
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#004777",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        1
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#334155", lineHeight: 1.4 }}>
                        <strong>Show Pass:</strong> Drive up to the Prime Park gate and present this Ticket ID (<strong>{ticketNo}</strong>).
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#004777",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        2
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#334155", lineHeight: 1.4 }}>
                        <strong>Vehicle Photos:</strong> Take 360° pictures of your vehicle at drop-off before handing over keys.
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#004777",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        3
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#334155", lineHeight: 1.4 }}>
                        <strong>Hand Over Keys:</strong> Leave your car key with the valet attendant for secure parking.
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#004777",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        4
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#334155", lineHeight: 1.4 }}>
                        <strong>Board Free Shuttle:</strong> Hop onto the shuttle directly to your JFK airport terminal!
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Policies & Important Terms */}
                <Box
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "20px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <InfoOutlinedIcon sx={{ color: "#004777", fontSize: "20px" }} />
                    <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>
                      Important Policies
                    </Typography>
                  </Box>

                  {/* Strict No-Refund Policy Alert */}
                  <Box
                    sx={{
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "12px",
                      p: 1.75,
                      mb: 2,
                      display: "flex",
                      gap: 1.25,
                      alignItems: "flex-start",
                    }}
                  >
                    <WarningAmberRoundedIcon sx={{ color: "#dc2626", fontSize: "20px", flexShrink: 0, mt: 0.2 }} />
                    <Typography sx={{ fontSize: "12.5px", color: "#991b1b", fontWeight: 600, lineHeight: 1.45 }}>
                      <strong>Strict No-Refund Policy:</strong> All online fees and deposit payments are strictly final and non-refundable.
                    </Typography>
                  </Box>

                  {/* Policy Highlights */}
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      color: "#475569",
                      fontSize: "12px",
                      lineHeight: 1.6,
                      "& li": { mb: 1 },
                    }}
                  >
                    <li>
                      <strong>Oversized Vehicles:</strong> Small SUV (+$5/day), Midsize/Large SUV (+$7/day), and Trucks (+$10/day) are subject to additional on-site surcharge.
                    </li>
                    <li>
                      <strong>Vehicle Condition Photos:</strong> Customers are required to take pictures of their vehicles (all sides) at drop-off; no damage claims can be accepted without these photos.
                    </li>
                    <li>
                      <strong>Key Requirement:</strong> Car key must be left with the attendant. Failure to do so may result in towing fees from $75 to $150.
                    </li>
                    <li>
                      <strong>No In/Out Privileges:</strong> Facility does not permit multiple entries/exits under one voucher.
                    </li>
                    <li>
                      <strong>Expired Vouchers:</strong> Overstay beyond reserved checkout is subject to standard facility rates ($10/hr or $15-$18/day).
                    </li>
                  </Box>
                </Box>

                {/* Complete Registration Callout for Guests */}
                {isGuest && (
                  <Box
                    sx={{
                      mt: 3,
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #001b44 0%, #004777 100%)",
                      color: "white",
                      boxShadow: "0 10px 25px rgba(0, 71, 119, 0.25)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
                      <PersonOutlineOutlinedIcon sx={{ color: "#38bdf8", fontSize: "22px" }} />
                      <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "white" }}>
                        Complete Your Account
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "12.5px", color: "#cbd5e1", lineHeight: 1.5, mb: 2 }}>
                      Save your account password to easily view your booking history, re-download receipts anytime, and enjoy faster future bookings.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate("/customer-registration")}
                      endIcon={<ArrowForwardOutlinedIcon />}
                      sx={{
                        backgroundColor: "#10b981",
                        color: "white",
                        borderRadius: "100px",
                        py: 1.3,
                        fontWeight: 800,
                        fontSize: "14px",
                        textTransform: "none",
                        boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                        "&:hover": {
                          backgroundColor: "#059669",
                        },
                      }}
                    >
                      Complete Registration
                    </Button>
                  </Box>
                )}

                {/* Secondary Navigation Options */}
                <Box sx={{ mt: 3, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <Button
                    variant="text"
                    startIcon={<ArrowBackOutlinedIcon />}
                    onClick={() => navigate("/booking-create")}
                    sx={{
                      color: "#004777",
                      fontWeight: 700,
                      textTransform: "none",
                      fontSize: "14px",
                      "&:hover": {
                        backgroundColor: "#f0f7ff",
                      },
                    }}
                  >
                    Book Another Spot
                  </Button>
                  {!isGuest && (
                    <Button
                      variant="text"
                      endIcon={<ArrowForwardOutlinedIcon />}
                      onClick={() => navigate("/booking-list")}
                      sx={{
                        color: "#004777",
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "14px",
                        "&:hover": {
                          backgroundColor: "#f0f7ff",
                        },
                      }}
                    >
                      View All My Bookings
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </div>
    </div>
  );
};

export default BookingDetails;